import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { CircleMarker } from 'react-leaflet';
import { Plus, Trash2, Signpost, PencilRuler, X, Check, Globe, RefreshCw, Loader2 } from 'lucide-react';

import { Section } from '@/components/primitives/Section';
import { EmptyState } from '@/components/primitives/EmptyState';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

// Legacy JS api modules — typed via the permissive @/api/* shim.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
import * as streetAliasesApi from '@/api/streetAliases';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
import * as parcelsApi from '@/api/parcels';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
import * as osmRoadsApi from '@/api/osmRoads';

import { MapCanvas } from '@/components/maps/MapCanvas';
import type { DrawnEvent } from '@/components/maps/MapDrawControl';
import { buildAddressIndex } from '@/hooks/useAddressSearchIndex';
import { invalidateTenantStreetAliases } from '@/utils/streetAliasCache';
import { invalidateTenantOsmRoads } from '@/utils/osmRoadCache';
import { getParcelRef, parcelsNearLine, type ParcelRecord } from '@/utils/parcelGeometry';
import { parseApiError } from '@/lib/apiErrors';

interface StreetAliasRecord {
  id: number;
  tenant_id: number;
  alias: string;
  canonical_street_name?: string | null;
  parcel_refs?: string[] | null;
}

interface StreetsTabProps {
  tenantId: number;
}

// Polyline-only — a road is a line, not an area. Parcels are resolved by
// proximity to the traced centerline (parcelsNearLine), not by containment,
// so there's no reason to make the admin close a shape around a cluster:
// tracing the road itself is the more natural gesture, especially for a
// long or winding one.
const ROAD_LINE_DRAW = Object.freeze({
  rectangle: false,
  circle: false,
  circlemarker: false,
  marker: false,
  polygon: false,
  polyline: {
    shapeOptions: {
      color: '#7b1fa2',
      weight: 4,
    },
  },
});

/**
 * FPA Settings Hub tab for admin-curated street names — layered on top of
 * the cadastral StreetName data to cover what it can't express: the
 * community using an old/informal name for an official street (top
 * section), and unnamed gravel roads that have no StreetName at all
 * (bottom section — trace the road and name the parcels alongside it).
 */
export default function StreetsTab({ tenantId }: StreetsTabProps) {
  const [aliases, setAliases] = useState<StreetAliasRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Tenant's parcels, fetched once without geometry — this tab only needs
  // StreetName/centroid, not per-parcel outlines (MapCanvas's own
  // ParcelOverlay fetches full geometry separately for the visual render).
  const [parcels, setParcels] = useState<ParcelRecord[]>([]);

  const [canonicalStreetName, setCanonicalStreetName] = useState('');
  const [aliasText, setAliasText] = useState('');
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);

  // ── Unnamed-road tagging state ───────────────────────────────────────── //
  const [tagging, setTagging] = useState(false);
  const [selectedParcels, setSelectedParcels] = useState<ParcelRecord[]>([]);
  const [roadNameDialogOpen, setRoadNameDialogOpen] = useState(false);
  const [roadName, setRoadName] = useState('');
  const [savingRoad, setSavingRoad] = useState(false);

  // ── OSM roads (Overpass-sourced, fills gaps the cadastral data leaves) ── //
  const [osmRoadCount, setOsmRoadCount] = useState<number | null>(null);
  const [osmRoadVersion, setOsmRoadVersion] = useState<string | null>(null);
  const [refreshingOsmRoads, setRefreshingOsmRoads] = useState(false);

  const refreshOsmRoadStatus = useCallback(async () => {
    try {
      const data = await osmRoadsApi.listOsmRoads(tenantId);
      setOsmRoadCount(data?.total ?? 0);
      setOsmRoadVersion(data?.version ?? null);
    } catch (err) {
      console.warn('Failed to load OSM road status:', err);
    }
  }, [tenantId]);

  useEffect(() => {
    refreshOsmRoadStatus();
  }, [refreshOsmRoadStatus]);

  const handleRefreshOsmRoads = async () => {
    setRefreshingOsmRoads(true);
    try {
      const result = await osmRoadsApi.refreshOsmRoads(tenantId);
      toast.success(`Found ${result.road_count} named road${result.road_count === 1 ? '' : 's'} from OpenStreetMap`);
      invalidateTenantOsmRoads(tenantId);
      await refreshOsmRoadStatus();
    } catch (err) {
      toast.error(parseApiError(err, 'Refresh failed').message);
    } finally {
      setRefreshingOsmRoads(false);
    }
  };

  const refresh = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await streetAliasesApi.listStreetAliases({
        tenant_id: tenantId,
        // Backend caps page_size at 100 (matches suburbs/boundaries) — this
        // used to say 200, which 422'd every load (see parseApiError below:
        // an unhandled 422 array of Pydantic errors crashed the whole tab).
        page_size: 100,
      });
      setAliases(data?.street_aliases ?? []);
    } catch (err) {
      setLoadError(parseApiError(err, 'Load failed').message);
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await parcelsApi.listTenantParcels(tenantId, {
          include_geometry: false,
        });
        if (cancelled) return;
        setParcels((data?.parcels ?? []) as ParcelRecord[]);
      } catch (err) {
        console.warn('Failed to load parcels for the Streets tab:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  const streetNames = useMemo(() => {
    const index = buildAddressIndex(parcels);
    return index.groups.map((g) => g.displayName).sort((a, b) => a.localeCompare(b));
  }, [parcels]);

  const unnamedParcels = useMemo(
    () => parcels.filter((p) => !p.StreetName || !p.StreetName.trim()),
    [parcels],
  );

  const unnamedMarkerHints = useMemo(
    () =>
      unnamedParcels.map((p) => ({
        position:
          p.centroid_lat != null && p.centroid_lon != null
            ? ([p.centroid_lat, p.centroid_lon] as [number, number])
            : null,
      })),
    [unnamedParcels],
  );

  // ── Alias-to-existing-street form ────────────────────────────────────── //

  const handleCreate = async () => {
    const street = canonicalStreetName.trim();
    const alias = aliasText.trim();
    if (!street || !alias) return;
    setSaving(true);
    try {
      await streetAliasesApi.createStreetAlias({
        tenant_id: tenantId,
        alias,
        canonical_street_name: street,
      });
      toast.success(`"${alias}" added as an alias for ${street}`);
      setCanonicalStreetName('');
      setAliasText('');
      invalidateTenantStreetAliases(tenantId);
      await refresh();
    } catch (err) {
      toast.error(parseApiError(err, 'Save failed').message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (a: StreetAliasRecord) => {
    if (!window.confirm(`Delete alias "${a.alias}"?`)) return;
    setBusyId(a.id);
    try {
      await streetAliasesApi.deleteStreetAlias(a.id);
      toast.success(`Alias "${a.alias}" deleted`);
      invalidateTenantStreetAliases(tenantId);
      await refresh();
    } catch (err) {
      toast.error(parseApiError(err, 'Delete failed').message);
    } finally {
      setBusyId(null);
    }
  };

  const canSave = canonicalStreetName.trim() !== '' && aliasText.trim() !== '' && !saving;

  // ── Unnamed-road tagging ─────────────────────────────────────────────── //

  const enterTagging = () => {
    setSelectedParcels([]);
    setTagging(true);
  };

  const exitTagging = () => {
    setSelectedParcels([]);
    setTagging(false);
  };

  const handleDrawCreated = useCallback(
    (e: DrawnEvent) => {
      if (!e.positions) return;
      const hits = parcelsInRing(e.positions, unnamedParcels);
      setSelectedParcels(hits);
    },
    [unnamedParcels],
  );

  const commitRoadName = async () => {
    const name = roadName.trim();
    if (!name || selectedParcels.length === 0) return;
    const refs = selectedParcels
      .map((p) => getParcelRef(p))
      .filter((r): r is string => Boolean(r));
    if (refs.length === 0) return;
    setSavingRoad(true);
    try {
      await streetAliasesApi.createStreetAlias({
        tenant_id: tenantId,
        alias: name,
        parcel_refs: refs,
      });
      toast.success(`"${name}" saved for ${refs.length} parcel${refs.length === 1 ? '' : 's'}`);
      setRoadNameDialogOpen(false);
      setRoadName('');
      invalidateTenantStreetAliases(tenantId);
      exitTagging();
      await refresh();
    } catch (err) {
      toast.error(parseApiError(err, 'Save failed').message);
    } finally {
      setSavingRoad(false);
    }
  };

  return (
    <div className="space-y-4">
      <Section
        title="Add a street alias"
        description="Attach an old or informal name to an official street — the map search will match either."
      >
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[220px] flex-1 space-y-1.5">
            <Label htmlFor="street-alias-canonical">Official street</Label>
            <Input
              id="street-alias-canonical"
              list="street-alias-street-names"
              value={canonicalStreetName}
              onChange={(e) => setCanonicalStreetName(e.target.value)}
              placeholder="e.g. Church Street"
            />
            <datalist id="street-alias-street-names">
              {streetNames.map((name) => (
                <option key={name} value={name} />
              ))}
            </datalist>
          </div>
          <div className="min-w-[220px] flex-1 space-y-1.5">
            <Label htmlFor="street-alias-name">Alias / old name</Label>
            <Input
              id="street-alias-name"
              value={aliasText}
              onChange={(e) => setAliasText(e.target.value)}
              placeholder="e.g. Kerk Straat"
            />
          </div>
          <Button onClick={handleCreate} disabled={!canSave} className="gap-1">
            <Plus className="h-4 w-4" />
            {saving ? 'Adding…' : 'Add alias'}
          </Button>
        </div>
      </Section>

      <Section
        title="Name an unnamed road"
        description="Some gravel roads have no official street name at all — draw around their parcels and give the road a name."
        actions={
          tagging ? (
            <Button size="sm" variant="outline" onClick={exitTagging}>
              Cancel
            </Button>
          ) : (
            <Button
              size="sm"
              onClick={enterTagging}
              disabled={unnamedParcels.length === 0}
              className="gap-1"
            >
              <PencilRuler className="h-4 w-4" />
              Start tagging
            </Button>
          )
        }
        unpadded={tagging}
      >
        {!tagging ? (
          unnamedParcels.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Every parcel in this FPA already has an official street name.
            </p>
          ) : (
            <p className="text-sm text-muted-foreground">
              {unnamedParcels.length} parcel{unnamedParcels.length === 1 ? '' : 's'} in this FPA
              have no official street name yet.
            </p>
          )
        ) : (
          <div className="relative h-[60vh] min-h-[420px] overflow-hidden rounded-b-lg">
            <MapCanvas
              center={[-25.6429, 28.3433]}
              zoom={9}
              variant="lg"
              tenantId={tenantId}
              forceParcels
              mapSearch
              className="!h-full !min-h-0 !rounded-none"
              drawing={{
                enabled: true,
                position: 'topleft',
                drawOptions: PARCEL_SELECT_DRAW,
                onCreated: handleDrawCreated,
              }}
              fitBoundsTo={{ markers: unnamedMarkerHints }}
            >
              {selectedParcels.map((p) => {
                const ref = getParcelRef(p);
                if (p.centroid_lat == null || p.centroid_lon == null) return null;
                return (
                  <CircleMarker
                    key={ref ?? `${p.centroid_lat},${p.centroid_lon}`}
                    center={[p.centroid_lat, p.centroid_lon]}
                    radius={8}
                    pathOptions={{ color: '#7b1fa2', fillColor: '#ce93d8', fillOpacity: 0.8, weight: 2 }}
                  />
                );
              })}
            </MapCanvas>

            {/* Selection banner — floats over the map, mirrors BoundariesTab's
                drawing-status banner. */}
            <div className="pointer-events-none absolute inset-x-0 bottom-3 z-[1000] flex justify-center">
              <div className="pointer-events-auto flex items-center gap-3 rounded-full border bg-background/95 px-4 py-2 shadow-lg backdrop-blur">
                {selectedParcels.length === 0 ? (
                  <span className="text-sm text-muted-foreground">
                    Draw a shape around the road's parcels (unnamed parcels only)
                  </span>
                ) : (
                  <Badge variant="secondary" className="text-sm">
                    {selectedParcels.length} parcel{selectedParcels.length === 1 ? '' : 's'} selected
                  </Badge>
                )}
                <Button size="sm" variant="ghost" className="gap-1" onClick={exitTagging}>
                  <X className="h-4 w-4" />
                  Discard
                </Button>
                <Button
                  size="sm"
                  className="gap-1"
                  disabled={selectedParcels.length === 0}
                  onClick={() => setRoadNameDialogOpen(true)}
                >
                  <Check className="h-4 w-4" />
                  Name this road
                </Button>
              </div>
            </div>
          </div>
        )}
      </Section>

      <Section
        title="Roads from OpenStreetMap"
        description="Fills in roads with no cadastral street name at all (e.g. one labelled on the map but missing from the property data) — refresh occasionally, not something to run on every visit."
        actions={
          <Button
            size="sm"
            variant="outline"
            onClick={handleRefreshOsmRoads}
            disabled={refreshingOsmRoads}
            className="gap-1"
          >
            {refreshingOsmRoads ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            Refresh
          </Button>
        }
      >
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Globe className="h-4 w-4" />
          {osmRoadCount == null
            ? 'Loading…'
            : osmRoadCount === 0
              ? 'No roads cached yet — click Refresh to fetch from OpenStreetMap.'
              : `${osmRoadCount} road${osmRoadCount === 1 ? '' : 's'} cached`}
          {osmRoadVersion && (
            <span>
              · last refreshed {new Date(osmRoadVersion).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}
            </span>
          )}
        </div>
      </Section>

      <Section
        title="Street aliases"
        description={aliases.length ? `${aliases.length} defined` : 'No aliases yet.'}
      >
        {loadError && (
          <Alert variant="destructive" className="mb-3">
            <AlertDescription>{loadError}</AlertDescription>
          </Alert>
        )}
        {loading ? (
          <div className="space-y-2">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        ) : aliases.length === 0 ? (
          <EmptyState
            icon={Signpost}
            title="No street aliases yet"
            description="Add one above when the community uses a different name for a street, or tag an unnamed road."
          />
        ) : (
          <ul className="space-y-2">
            {aliases.map((a) => (
              <li
                key={a.id}
                className="flex items-start justify-between gap-3 rounded-md border p-3"
              >
                <div className="min-w-0 flex-1">
                  <span className="truncate font-medium">{a.alias}</span>
                  {a.canonical_street_name && (
                    <p className="mt-1 text-xs text-muted-foreground">
                      Alias for <span className="font-medium">{a.canonical_street_name}</span>
                    </p>
                  )}
                  {a.parcel_refs && a.parcel_refs.length > 0 && (
                    <p className="mt-1 text-xs text-muted-foreground">
                      Custom name for {a.parcel_refs.length} parcel
                      {a.parcel_refs.length === 1 ? '' : 's'}
                    </p>
                  )}
                </div>
                <Button
                  size="icon"
                  variant="ghost"
                  title="Delete"
                  disabled={busyId === a.id}
                  onClick={() => handleDelete(a)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </Section>

      {/* ── Road-name dialog (post-draw) ──────────────────────────────── */}
      <Dialog open={roadNameDialogOpen} onOpenChange={setRoadNameDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Name this road</DialogTitle>
            <DialogDescription>
              {selectedParcels.length} parcel{selectedParcels.length === 1 ? '' : 's'} selected.
            </DialogDescription>
          </DialogHeader>
          <Input
            value={roadName}
            autoFocus
            placeholder="e.g. Old Quarry Road"
            onChange={(e) => setRoadName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && roadName.trim() && !savingRoad) commitRoadName();
            }}
          />
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRoadNameDialogOpen(false)}
              disabled={savingRoad}
            >
              Cancel
            </Button>
            <Button onClick={commitRoadName} disabled={!roadName.trim() || savingRoad}>
              {savingRoad ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
